import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Profile } from '@par/shared/types/player';
import { getAveColor } from '../common/constants';
import { fetchGet } from '../api/common';
import { Game } from '@par/shared/types/game';

type DailiesSummary = {
  totalDailiesPlayed: number;
  previousSevenDailies: Game[];
};

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    textAlign: 'center',
  },
}));

type PropTypes = {
  closeDailiesProfile: () => void;
  openDailiesProfile: boolean;
  viewProfile: Profile;
};

export const DailiesProfile = ({
  closeDailiesProfile,
  openDailiesProfile,
  viewProfile,
}: PropTypes) => {
  const [dailiesSummary, setDailiesSummary] = useState<DailiesSummary>();
  const [daysOfWeekSummary, setDaysOfWeekSummary] = useState<
    Record<string, any>
  >({
    sun: null,
    mon: null,
    tue: null,
    wed: null,
    thu: null,
    fri: null,
    sat: null,
  });
  const theme = useTheme();

  useEffect(() => {
    fetchDailiesSummary();
  }, [openDailiesProfile]);

  const fetchDailiesSummary = async () => {
    const res: DailiesSummary = await fetchGet(
      `/games/dailies-summary/${viewProfile.id}`
    );
    bucketInDaysOfWeek(res.previousSevenDailies);
    setDailiesSummary(res);
  };

  const bucketInDaysOfWeek = (games: Game[]) => {
    const dayOfWeekScore: Record<string, any> = {
      sun: null,
      mon: null,
      tue: null,
      wed: null,
      thu: null,
      fri: null,
      sat: null,
    };
    const currentDayOfWeek = new Date().getDay();
    for (let i = 0; i <= currentDayOfWeek; i++) {
      const found = games.find((g) => new Date(g.createdAt).getDay() === i);
      let score;
      if (found) {
        score = found.players[viewProfile.id]
          ? found.players[viewProfile.id].parScore
          : null;
      } else {
        if (i === currentDayOfWeek) {
          score = null;
        } else {
          score = 'missed';
        }
      }
      switch (i) {
        case 0:
          dayOfWeekScore.sun = score;
          break;
        case 1:
          dayOfWeekScore.mon = score;
          break;
        case 2:
          dayOfWeekScore.tue = score;
          break;
        case 3:
          dayOfWeekScore.wed = score;
          break;
        case 4:
          dayOfWeekScore.thu = score;
          break;
        case 5:
          dayOfWeekScore.fri = score;
          break;
        case 6:
          dayOfWeekScore.sat = score;
          break;
        default:
          break;
      }
    }
    setDaysOfWeekSummary(dayOfWeekScore);
  };

  return (
    <Dialog
      open={openDailiesProfile}
      onClose={closeDailiesProfile}
      maxWidth="md"
      sx={{
        '& .paper': {
          borderStyle: 'solid',
          borderColor: theme.palette.ranked.main,
        },
      }}
    >
      <DialogTitle>
        <Stack spacing={2}>
          <Typography
            variant="h5"
            textAlign="center"
            sx={{
              textShadow:
                theme.palette.mode === 'dark'
                  ? `0 0 0px ${theme.palette.dailies.main}, 0 0 0px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                  : 'none',
            }}
          >
            {viewProfile.username}'s
          </Typography>
          <Typography
            variant="h4"
            textAlign="center"
            mb={2}
            sx={{
              textShadow:
                theme.palette.mode === 'dark'
                  ? `0 0 0px ${theme.palette.dailies.main}, 0 0 0px ${theme.palette.dailies.main}, 0px 0px 16px ${theme.palette.dailies.main}`
                  : 'none',
            }}
          >
            Dailies Summary
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ width: '100%', height: '100%' }}>
        <Typography variant="h5" textAlign={'center'} mt={2} mb={2}>
          Total Dailies Played
        </Typography>
        <Typography
          variant="h4"
          textAlign={'center'}
          fontWeight={600}
          mt={2}
          mb={2}
        >
          {dailiesSummary?.totalDailiesPlayed}
        </Typography>
        <>
          <Typography variant="h5" textAlign={'center'} mt={5} mb={2}>
            Scores This Week
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="simple table">
              <TableBody>
                <TableRow>
                  <StyledTableCell
                    sx={{ color: getAveColor('par', theme.palette.mode) }}
                  >
                    SUN
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ color: getAveColor('birdie', theme.palette.mode) }}
                  >
                    MON
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ color: getAveColor('eagle', theme.palette.mode) }}
                  >
                    TUE
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ color: getAveColor('albatross', theme.palette.mode) }}
                  >
                    WED
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ color: getAveColor('condor', theme.palette.mode) }}
                  >
                    THU
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ color: getAveColor('ostrich', theme.palette.mode) }}
                  >
                    FRI
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      color: getAveColor('pterodactyl', theme.palette.mode),
                    }}
                  >
                    SAT
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell
                    sx={{
                      color:
                        daysOfWeekSummary?.sun !== null
                          ? getAveColor('par', theme.palette.mode)
                          : 'none',
                    }}
                  >
                    {daysOfWeekSummary?.sun === 'missed'
                      ? 'miss'
                      : daysOfWeekSummary?.sun !== null
                      ? (daysOfWeekSummary?.sun > 0 ? '+' : '') +
                        daysOfWeekSummary?.sun.toString()
                      : 'TBD'}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      color:
                        daysOfWeekSummary?.mon !== null
                          ? getAveColor('birdie', theme.palette.mode)
                          : 'none',
                    }}
                  >
                    {daysOfWeekSummary?.mon === 'missed'
                      ? 'miss'
                      : daysOfWeekSummary?.mon !== null
                      ? (daysOfWeekSummary?.mon > 0 ? '+' : '') +
                        daysOfWeekSummary?.mon.toString()
                      : 'TBD'}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      color:
                        daysOfWeekSummary?.tue !== null
                          ? getAveColor('eagle', theme.palette.mode)
                          : 'none',
                    }}
                  >
                    {daysOfWeekSummary?.tue === 'missed'
                      ? 'miss'
                      : daysOfWeekSummary?.tue !== null
                      ? (daysOfWeekSummary?.tue > 0 ? '+' : '') +
                        daysOfWeekSummary?.tue.toString()
                      : 'TBD'}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      color:
                        daysOfWeekSummary?.wed !== null
                          ? getAveColor('albatross', theme.palette.mode)
                          : 'none',
                    }}
                  >
                    {daysOfWeekSummary?.wed === 'missed'
                      ? 'miss'
                      : daysOfWeekSummary?.wed !== null
                      ? (daysOfWeekSummary?.wed > 0 ? '+' : '') +
                        daysOfWeekSummary?.wed.toString()
                      : 'TBD'}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      color:
                        daysOfWeekSummary?.thu !== null
                          ? getAveColor('condor', theme.palette.mode)
                          : 'none',
                    }}
                  >
                    {daysOfWeekSummary?.thu === 'missed'
                      ? 'miss'
                      : daysOfWeekSummary?.thu !== null
                      ? (daysOfWeekSummary?.thu > 0 ? '+' : '') +
                        daysOfWeekSummary?.thu.toString()
                      : 'TBD'}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      color:
                        daysOfWeekSummary?.fri !== null
                          ? getAveColor('ostrich', theme.palette.mode)
                          : 'none',
                    }}
                  >
                    {daysOfWeekSummary?.fri === 'missed'
                      ? 'miss'
                      : daysOfWeekSummary?.fri !== null
                      ? (daysOfWeekSummary?.fri > 0 ? '+' : '') +
                        daysOfWeekSummary?.fri.toString()
                      : 'TBD'}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      color:
                        daysOfWeekSummary?.sat !== null
                          ? getAveColor('pterodactyl', theme.palette.mode)
                          : 'none',
                    }}
                  >
                    {daysOfWeekSummary?.sat === 'missed'
                      ? 'miss'
                      : daysOfWeekSummary?.sat !== null
                      ? (daysOfWeekSummary?.sat > 0 ? '+' : '') +
                        daysOfWeekSummary?.sat.toString()
                      : 'TBD'}
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
        <Typography variant="h5" textAlign={'center'} mt={5} mb={2}>
          Current Streaks
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small" aria-label="simple table">
            <TableBody>
              <TableRow>
                <StyledTableCell width={'33%'}>
                  Consecutive Played
                </StyledTableCell>
                <StyledTableCell width={'33%'}>No Timeouts</StyledTableCell>
                <Tooltip title="Finish a game with 0 or under">
                  <StyledTableCell width={'33%'}>Scratches</StyledTableCell>
                </Tooltip>
              </TableRow>
              <TableRow>
                <StyledTableCell sx={{ fontSize: 20 }}>
                  {viewProfile.dailiesStats.consecutiveDailiesPlayedCurrent !==
                  undefined
                    ? viewProfile.dailiesStats.consecutiveDailiesPlayedCurrent
                    : '--'}
                </StyledTableCell>
                <StyledTableCell sx={{ fontSize: 20 }}>
                  {viewProfile.dailiesStats
                    .consecutiveDailiesNoTimeOutsCurrent !== undefined
                    ? viewProfile.dailiesStats
                        .consecutiveDailiesNoTimeOutsCurrent
                    : '--'}
                </StyledTableCell>
                <StyledTableCell sx={{ fontSize: 20 }}>
                  {viewProfile.dailiesStats
                    .consecutiveDailiesParOrBetterCurrent !== undefined
                    ? viewProfile.dailiesStats
                        .consecutiveDailiesParOrBetterCurrent
                    : '--'}
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="h5" textAlign={'center'} mt={5} mb={2}>
          All Time Streaks
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small" aria-label="simple table">
            <TableBody>
              <TableRow>
                <StyledTableCell width={'33%'}>
                  Consecutive Played
                </StyledTableCell>
                <StyledTableCell width={'33%'}>No Timeouts</StyledTableCell>
                <Tooltip title="Finish a game with 0 or under">
                  <StyledTableCell width={'33%'}>Scratches</StyledTableCell>
                </Tooltip>
              </TableRow>
              <TableRow>
                <StyledTableCell sx={{ fontSize: 20 }}>
                  {viewProfile.dailiesStats.consecutiveDailiesPlayedAllTime !==
                  undefined
                    ? viewProfile.dailiesStats.consecutiveDailiesPlayedAllTime
                    : '--'}
                </StyledTableCell>
                <StyledTableCell sx={{ fontSize: 20 }}>
                  {viewProfile.dailiesStats
                    .consecutiveDailiesNoTimeOutsAllTime !== undefined
                    ? viewProfile.dailiesStats
                        .consecutiveDailiesNoTimeOutsAllTime
                    : '--'}
                </StyledTableCell>
                <StyledTableCell sx={{ fontSize: 20 }}>
                  {viewProfile.dailiesStats
                    .consecutiveDailiesParOrBetterAllTime !== undefined
                    ? viewProfile.dailiesStats
                        .consecutiveDailiesParOrBetterAllTime
                    : '--'}
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={closeDailiesProfile} fullWidth>
          close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
